<template>
  <div class="pg-interp">
    <!-- Interpretation Exercises -->
    <div class="pg-interp__section">
      <div class="pg-interp__section-label">Interpretation Exercises</div>

      <div class="pg-interp__list">
        <div
          v-for="(exercise, index) in sectionData.content.exercises"
          :key="index"
          class="pg-interp__item"
        >
          <div class="pg-interp__item-head">
            <span class="pg-interp__index">Exercise {{ index + 1 }}</span>
            <div class="pg-interp__item-actions">
              <button
                type="button"
                class="pg-interp__icon-btn pg-interp__icon-btn--danger"
                title="제거"
                @click.prevent="removeExercise(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>

          <div class="pg-interp__item-body">
            <!-- Language Settings -->
            <div class="pg-interp__group">
              <div class="pg-interp__group-label">Language Settings</div>
              <div class="p-form-grid p-form-grid--2">
                <div class="p-field">
                  <label class="p-field__label">Source Language</label>
                  <select v-model="exercise.sourceLanguage" class="p-input">
                    <option value="">언어 선택</option>
                    <option value="ko">한국어 (Korean)</option>
                    <option value="en">English</option>
                    <option value="ja">日本語 (Japanese)</option>
                    <option value="zh">中文 (Chinese)</option>
                    <option value="vi">Tiếng Việt (Vietnamese)</option>
                    <option value="mn">Монгол хэл (Mongolian)</option>
                    <option value="ru">Русский (Russian)</option>
                  </select>
                </div>
                <div class="p-field">
                  <label class="p-field__label">Target Language</label>
                  <select v-model="exercise.targetLanguage" class="p-input">
                    <option value="">언어 선택</option>
                    <option value="ko">한국어 (Korean)</option>
                    <option value="en">English</option>
                    <option value="ja">日本語 (Japanese)</option>
                    <option value="zh">中文 (Chinese)</option>
                    <option value="vi">Tiếng Việt (Vietnamese)</option>
                    <option value="mn">Монгол хэл (Mongolian)</option>
                    <option value="ru">Русский (Russian)</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Content Section -->
            <div class="pg-interp__group">
              <div class="pg-interp__group-label">Content &amp; Translation</div>
              <div class="p-form-grid p-form-grid--2">
                <div class="pg-interp__text-block">
                  <div class="p-field">
                    <label class="p-field__label">Source Text</label>
                    <textarea
                      v-model="exercise.sourceText"
                      class="p-input p-input--area"
                      rows="4"
                      placeholder="통역할 원문을 입력하세요..."
                      @input="updateWordCount(exercise)"
                    />
                  </div>
                  <div class="p-field">
                    <label class="p-field__label">Source 인정 답안</label>
                    <textarea
                      v-model="exercise.sourceAcceptedAnswers"
                      class="p-input p-input--area"
                      rows="2"
                      placeholder="인정 답안을 쉼표로 구분하여 입력하세요 (예: 필요 서류를 표시하다, 제증명 신청서에 필요 서류를 표시하다)"
                    />
                  </div>
                </div>
                <div class="pg-interp__text-block">
                  <div class="p-field">
                    <label class="p-field__label">Expected Translation</label>
                    <textarea
                      v-model="exercise.expectedTranslation"
                      class="p-input p-input--area"
                      rows="4"
                      placeholder="예상 번역문을 입력하세요..."
                    />
                  </div>
                  <div class="p-field">
                    <label class="p-field__label">Expected 인정 답안</label>
                    <textarea
                      v-model="exercise.expectedAcceptedAnswers"
                      class="p-input p-input--area"
                      rows="2"
                      placeholder="인정 답안을 쉼표로 구분하여 입력하세요 (예: to check off the required documents, to mark the required documents)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Exercise Settings -->
            <div class="pg-interp__group">
              <div class="pg-interp__group-label">Exercise Settings</div>

              <div class="p-field">
                <label class="p-field__label">Difficulty</label>
                <div class="pg-interp__tabs">
                  <button
                    type="button"
                    class="pg-interp__tab"
                    :class="{ 'pg-interp__tab--active': exercise.difficulty === 'beginner' }"
                    @click.prevent="exercise.difficulty = 'beginner'"
                  >
                    초급 (Beginner)
                  </button>
                  <button
                    type="button"
                    class="pg-interp__tab"
                    :class="{ 'pg-interp__tab--active': exercise.difficulty === 'intermediate' }"
                    @click.prevent="exercise.difficulty = 'intermediate'"
                  >
                    중급 (Intermediate)
                  </button>
                  <button
                    type="button"
                    class="pg-interp__tab"
                    :class="{ 'pg-interp__tab--active': exercise.difficulty === 'advanced' }"
                    @click.prevent="exercise.difficulty = 'advanced'"
                  >
                    고급 (Advanced)
                  </button>
                  <button
                    type="button"
                    class="pg-interp__tab"
                    :class="{ 'pg-interp__tab--active': exercise.difficulty === 'expert' }"
                    @click.prevent="exercise.difficulty = 'expert'"
                  >
                    전문가 (Expert)
                  </button>
                </div>
              </div>

              <div class="p-form-grid p-form-grid--4">
                <div class="p-field">
                  <label class="p-field__label">Context Type</label>
                  <select v-model="exercise.contextType" class="p-input">
                    <option value="conversation">대화 (Conversation)</option>
                    <option value="presentation">발표 (Presentation)</option>
                    <option value="interview">인터뷰 (Interview)</option>
                    <option value="meeting">회의 (Meeting)</option>
                    <option value="lecture">강의 (Lecture)</option>
                    <option value="news">뉴스 (News)</option>
                  </select>
                </div>
                <div class="p-field">
                  <label class="p-field__label">Time Limit (sec)</label>
                  <input
                    type="number"
                    v-model.number="exercise.timeLimit"
                    class="p-input"
                    min="30"
                    max="600"
                    placeholder="제한 시간"
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">Time Display</label>
                  <div class="pg-interp__badge pg-interp__badge--time">
                    {{ formatTime(exercise.timeLimit) }}
                  </div>
                </div>
                <div class="p-field">
                  <label class="p-field__label">Word Count</label>
                  <div class="pg-interp__badge pg-interp__badge--ok">
                    {{ exercise.wordCount || 0 }} words
                  </div>
                </div>
              </div>

              <div class="p-field">
                <label class="p-field__label">Speed Level</label>
                <select v-model="exercise.speedLevel" class="p-input">
                  <option value="slow">느림 (Slow)</option>
                  <option value="normal">보통 (Normal)</option>
                  <option value="fast">빠름 (Fast)</option>
                  <option value="native">원어민 (Native)</option>
                </select>
              </div>
            </div>

            <!-- Additional Settings -->
            <div class="pg-interp__group">
              <div class="pg-interp__group-label">Additional</div>
              <div class="p-form-grid p-form-grid--2">
                <div class="p-field">
                  <label class="p-field__label">Context / Background</label>
                  <textarea
                    v-model="exercise.context"
                    class="p-input p-input--area"
                    rows="2"
                    placeholder="통역 상황이나 배경 정보를 입력하세요..."
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">Key Terms / Phrases</label>
                  <textarea
                    v-model="exercise.keyTerms"
                    class="p-input p-input--area"
                    rows="2"
                    placeholder="핵심 용어나 구문을 입력하세요 (한 줄에 하나씩)..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pg-interp__actions">
        <ABtn kind="yellow" icon="add" @click.prevent="addExercise">Interpretation Exercise 추가</ABtn>
        <ABtn kind="ghost" icon="upload_file" @click.prevent="triggerExcelUpload">Excel 일괄 등록</ABtn>
        <input
          ref="excelFileInput"
          type="file"
          accept=".xlsx, .xls"
          class="pg-interp__file"
          @change="handleExcelUpload"
        />
      </div>
    </div>

    <!-- Global Settings -->
    <div class="pg-interp__section">
      <div class="pg-interp__section-label">Global Settings</div>
      <div class="pg-interp__global">
        <div class="p-form-grid p-form-grid--3">
          <div class="p-field">
            <label class="p-field__label">Recording Quality</label>
            <select v-model="sectionData.content.recordingQuality" class="p-input">
              <option value="standard">표준 (Standard)</option>
              <option value="high">고품질 (High)</option>
              <option value="ultra">최고품질 (Ultra)</option>
            </select>
          </div>
          <div class="p-field">
            <label class="p-field__label">Allow Pause</label>
            <select v-model="sectionData.content.allowPause" class="p-input">
              <option :value="true">허용 (Allowed)</option>
              <option :value="false">불허 (Not Allowed)</option>
            </select>
          </div>
          <div class="p-field">
            <label class="p-field__label">Auto Evaluation</label>
            <select v-model="sectionData.content.autoEvaluation" class="p-input">
              <option :value="true">활성화 (Enabled)</option>
              <option :value="false">비활성화 (Disabled)</option>
            </select>
          </div>
        </div>
        <div class="pg-interp__hint">
          <i class="material-icons-round">info</i>
          전체 연습에 적용되는 공통 설정입니다.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineModel, watch, ref } from 'vue';
import * as XLSX from 'xlsx';
import ABtn from '@/components/Pineapple/ABtn.vue';

const sectionData = defineModel({ type: Object, required: true });
const excelFileInput = ref(null);

// 시간 포맷팅 함수
const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// 단어 수 계산 함수
const updateWordCount = (exercise) => {
  if (exercise.sourceText) {
    const words = exercise.sourceText.trim().split(/\s+/).filter(word => word.length > 0);
    exercise.wordCount = words.length;
  } else {
    exercise.wordCount = 0;
  }
};

// 데이터 구조 초기화
watch(() => sectionData.value.content, (newContent) => {
  if (!newContent.exercises) {
    sectionData.value.content.exercises = [];
  }
  if (!newContent.recordingQuality) {
    sectionData.value.content.recordingQuality = 'high';
  }
  if (newContent.allowPause === undefined) {
    sectionData.value.content.allowPause = true;
  }
  if (newContent.autoEvaluation === undefined) {
    sectionData.value.content.autoEvaluation = true;
  }

  // 기존 단일 연습용 필드들 제거
  if (newContent.sourceLanguage !== undefined) {
    delete sectionData.value.content.sourceLanguage;
  }
  if (newContent.targetLanguage !== undefined) {
    delete sectionData.value.content.targetLanguage;
  }
  if (newContent.sourceText !== undefined) {
    delete sectionData.value.content.sourceText;
  }
  if (newContent.expectedTranslation !== undefined) {
    delete sectionData.value.content.expectedTranslation;
  }
  if (newContent.timeLimit !== undefined) {
    delete sectionData.value.content.timeLimit;
  }
  if (newContent.difficulty !== undefined) {
    delete sectionData.value.content.difficulty;
  }
}, { immediate: true, deep: true });

const addExercise = () => {
  sectionData.value.content.exercises.push({
    sourceLanguage: '',
    targetLanguage: '',
    sourceText: '',
    sourceAcceptedAnswers: '',
    expectedTranslation: '',
    expectedAcceptedAnswers: '',
    timeLimit: 180,
    difficulty: 'intermediate',
    contextType: 'conversation',
    speedLevel: 'normal',
    context: '',
    keyTerms: '',
    wordCount: 0
  });
};

const removeExercise = (index) => {
  sectionData.value.content.exercises.splice(index, 1);
};

const triggerExcelUpload = () => {
  excelFileInput.value.click();
};

const handleExcelUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    let addedCount = 0;

    // 각 행을 순회하며 데이터 추가
    // Excel 컬럼 순서: Source Language, Target Language, Source Text, Source Accepted Answers,
    // Expected Translation, Expected Accepted Answers, Difficulty, Context Type, Time Limit, Speed Level, Context, Key Terms
    jsonData.forEach((row) => {
      // 빈 행은 건너뛰기
      if (!row || row.length === 0) return;

      const sourceLanguage = row[0] ? String(row[0]).trim() : '';
      const targetLanguage = row[1] ? String(row[1]).trim() : '';
      const sourceText = row[2] ? String(row[2]).trim() : '';
      const sourceAcceptedAnswers = row[3] ? String(row[3]).trim() : '';
      const expectedTranslation = row[4] ? String(row[4]).trim() : '';
      const expectedAcceptedAnswers = row[5] ? String(row[5]).trim() : '';
      const difficulty = row[6] ? String(row[6]).trim() : 'intermediate';
      const contextType = row[7] ? String(row[7]).trim() : 'conversation';
      const timeLimit = row[8] ? (isNaN(Number(row[8])) ? 180 : Number(row[8])) : 180;
      const speedLevel = row[9] ? String(row[9]).trim() : 'normal';
      const context = row[10] ? String(row[10]).trim() : '';
      const keyTerms = row[11] ? String(row[11]).trim() : '';

      // 최소한 sourceText나 expectedTranslation 중 하나에 데이터가 있으면 추가
      if (sourceText || expectedTranslation) {
        sectionData.value.content.exercises.push({
          sourceLanguage: sourceLanguage || '',
          targetLanguage: targetLanguage || '',
          sourceText: sourceText,
          sourceAcceptedAnswers: sourceAcceptedAnswers || '',
          expectedTranslation: expectedTranslation,
          expectedAcceptedAnswers: expectedAcceptedAnswers || '',
          timeLimit: timeLimit,
          difficulty: difficulty || 'intermediate',
          contextType: contextType || 'conversation',
          speedLevel: speedLevel || 'normal',
          context: context || '',
          keyTerms: keyTerms || '',
          wordCount: sourceText ? sourceText.trim().split(/\s+/).filter(word => word.length > 0).length : 0
        });
        addedCount++;
      }
    });

    alert(`${addedCount}개의 항목을 추가했습니다.`);

    // 파일 입력 초기화
    event.target.value = '';
  } catch (error) {
    console.error('Excel 파일 처리 오류:', error);
    alert('Excel 파일을 읽는 중 오류가 발생했습니다. 파일 형식을 확인해주세요.');
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-interp {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__section-label {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    letter-spacing: 0.02em;
    padding-bottom: 6px;
    border-bottom: 1px dashed $p-line;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__item-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
  }

  &__group-label {
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  &__text-block {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__tabs {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    padding: 3px;
  }

  &__tab {
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    padding: 6px 14px;
    border-radius: $p-radius-sm;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    &:hover {
      color: $p-ink;
    }

    &--active {
      background: $p-point;
      color: $p-ink;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 38px;
    padding: 0 14px;
    border-radius: $p-radius-sm;
    font-family: $p-font-mono;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;

    &--time {
      background: $p-ink;
      color: $p-point;
    }

    &--ok {
      background: $p-ok;
      color: #fff;
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__file {
    display: none;
  }

  &__global {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__hint {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: $p-panel-alt;
    border-radius: $p-radius-sm;
    border-left: 3px solid $p-info;
    font-size: 12px;
    color: $p-ink-mid;

    > i {
      color: $p-info;
      font-size: 16px;
    }
  }
}
</style>
